import { createSlice } from "@reduxjs/toolkit";
import {
  CategoryType,
  RequirementType,
  TestCaseType,
  MediaFile,
  EnvironmentDetails,
} from "./Types";

type initialStateType = {
  categories: CategoryType[];
  requirements: RequirementType[];
  testCases: TestCaseType[];
  selectedTestCase: TestCaseType | null;
  environmentDetails: EnvironmentDetails | null;
  environmentStatus: {
    loading: boolean;
    error: string | null;
  };
  historyEnvironmentDetails: EnvironmentDetails | null;
  historyEnvironmentStatus: {
    loading: boolean;
    error: string | null;
  };
  status: {
    categoriesLoading: boolean;
    requirementsLoading: boolean;
    testCasesLoading: boolean;
    categoryLoading: boolean;
    requirementLoading: boolean;
    testCaseLoading: boolean;
    createCategoryLoading: boolean;
    createRequirementLoading: boolean;
    createTestCaseLoading: boolean;
    updateCategoryLoading: boolean;
    updateRequirementLoading: boolean;
    updateTestCaseLoading: boolean;
    deleteCategoryLoading: boolean;
    deleteRequirementLoading: boolean;
    deleteTestCaseLoading: boolean;
  };
  mediaFiles: MediaFile[];
  mediaFilesIntegrationId: string | null;
  mediaFilesStatus: {
    loading: boolean;
    uploading: boolean;
    deleting: number | null;
    error: string | null;
  };
};

var initialState: initialStateType = {
  categories: [],
  requirements: [],
  testCases: [],
  selectedTestCase: null,
  environmentDetails: null,
  environmentStatus: {
    loading: false,
    error: null,
  },
  historyEnvironmentDetails: null,
  historyEnvironmentStatus: {
    loading: false,
    error: null,
  },
  status: {
    categoriesLoading: false,
    requirementsLoading: false,
    testCasesLoading: false,
    categoryLoading: false,
    requirementLoading: false,
    testCaseLoading: false,
    createCategoryLoading: false,
    createRequirementLoading: false,
    createTestCaseLoading: false,
    updateCategoryLoading: false,
    updateRequirementLoading: false,
    updateTestCaseLoading: false,
    deleteCategoryLoading: false,
    deleteRequirementLoading: false,
    deleteTestCaseLoading: false,
  },
  mediaFiles: [],
  mediaFilesIntegrationId: null,
  mediaFilesStatus: {
    loading: false,
    uploading: false,
    deleting: null,
    error: null,
  },
};

const projectValidationSlice = createSlice({
  name: "project-validation",
  initialState: initialState,
  reducers: {
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
    setRequirements: (state, action) => {
      state.requirements = action.payload;
    },
    setTestCases: (state, action) => {
      state.testCases = action.payload;
    },
    setSelectedTestCase: (state, action) => {
      state.selectedTestCase = action.payload;
    },
    // Environment details
    setEnvironmentDetails: (state, action) => {
      state.environmentDetails = action.payload;
    },
    setEnvironmentStatusLoading: (state, action) => {
      state.environmentStatus.loading = action.payload;
    },
    setEnvironmentStatusError: (state, action) => {
      state.environmentStatus.error = action.payload;
    },
    // History environment details (last submitted snapshot)
    setHistoryEnvironmentDetails: (state, action) => {
      state.historyEnvironmentDetails = action.payload;
    },
    setHistoryEnvironmentStatusLoading: (state, action) => {
      state.historyEnvironmentStatus.loading = action.payload;
    },
    setHistoryEnvironmentStatusError: (state, action) => {
      state.historyEnvironmentStatus.error = action.payload;
    },
    setCategoriesLoading: (state, action) => {
      state.status.categoriesLoading = action.payload;
    },
    setRequirementsLoading: (state, action) => {
      state.status.requirementsLoading = action.payload;
    },
    setTestCasesLoading: (state, action) => {
      state.status.testCasesLoading = action.payload;
    },
    setCategoryLoading: (state, action) => {
      state.status.categoryLoading = action.payload;
    },
    setRequirementLoading: (state, action) => {
      state.status.requirementLoading = action.payload;
    },
    setTestCaseLoading: (state, action) => {
      state.status.testCaseLoading = action.payload;
    },
    setCreateCategoryLoading: (state, action) => {
      state.status.createCategoryLoading = action.payload;
    },
    setCreateRequirementLoading: (state, action) => {
      state.status.createRequirementLoading = action.payload;
    },
    setCreateTestCaseLoading: (state, action) => {
      state.status.createTestCaseLoading = action.payload;
    },
    setUpdateCategoryLoading: (state, action) => {
      state.status.updateCategoryLoading = action.payload;
    },
    setUpdateRequirementLoading: (state, action) => {
      state.status.updateRequirementLoading = action.payload;
    },
    setUpdateTestCaseLoading: (state, action) => {
      state.status.updateTestCaseLoading = action.payload;
    },
    setDeleteCategoryLoading: (state, action) => {
      state.status.deleteCategoryLoading = action.payload;
    },
    setDeleteRequirementLoading: (state, action) => {
      state.status.deleteRequirementLoading = action.payload;
    },
    setDeleteTestCaseLoading: (state, action) => {
      state.status.deleteTestCaseLoading = action.payload;
    },
    setMediaFiles: (state, action) => {
      state.mediaFiles = action.payload;
    },
    setMediaFilesIntegrationId: (state, action) => {
      state.mediaFilesIntegrationId = action.payload;
    },
    setMediaFilesLoading: (state, action) => {
      state.mediaFilesStatus.loading = action.payload;
    },
    setMediaFilesUploading: (state, action) => {
      state.mediaFilesStatus.uploading = action.payload;
    },
    setMediaFilesDeleting: (state, action) => {
      state.mediaFilesStatus.deleting = action.payload;
    },
    setMediaFilesError: (state, action) => {
      state.mediaFilesStatus.error = action.payload;
    },
    resetEnvironmentalDetails: (state) => {
      state.environmentDetails = null;
      state.historyEnvironmentDetails = null;
    }
  },
});

export const projectValidationSliceReducer = projectValidationSlice.reducer;
export const projectValidationSliceAction = projectValidationSlice.actions;

